import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import { Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Send, Search, MessageSquare } from 'lucide-react';
import { useApi } from '../../api/useApi';
import { listMessages, listBookingMessages, sendBookingMessage } from '../../api/hostApi';
import { toast } from 'sonner';

export default function HostMessages() {
  const { data: threadsData, loading, error, refetch } = useApi(listMessages, []);
  const threads = useMemo(() => Array.isArray(threadsData) ? threadsData : (threadsData?.items || threadsData || []), [threadsData]);
  const [q, setQ] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState('');
  const [thread, setThread] = useState(null);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);

  const filtered = useMemo(() => threads.filter((item) => {
    const guest = item.customer || item.guest || {};
    const haystack = `${item.id} ${guest.name || ''} ${guest.email || ''} ${item.property?.title || ''}`.toLowerCase();
    return !q || haystack.includes(q.trim().toLowerCase());
  }), [threads, q]);

  useEffect(() => {
    if (!filtered.length) {
      setActiveId(null);
      return;
    }
    if (!activeId || !filtered.some((item) => Number(item.id) === Number(activeId))) setActiveId(filtered[0].id);
  }, [filtered, activeId]);

  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    let cancelled = false;
    setLoadingMsgs(true);
    listBookingMessages(activeId)
      .then((data) => { if (!cancelled) setThread(data || null); })
      .catch(() => { if (!cancelled) setThread(null); })
      .finally(() => { if (!cancelled) setLoadingMsgs(false); });
    return () => { cancelled = true; };
  }, [activeId]);

  const active = threads.find((item) => Number(item.id) === Number(activeId));
  const messages = thread?.messages || [];

  const send = async () => {
    const message = text.trim();
    if (!message || !activeId || sending) return;
    setSending(true);
    try {
      const newMessage = await sendBookingMessage(activeId, message);
      setThread((current) => ({ ...(current || {}), messages: [...(current?.messages || []), newMessage] }));
      setText('');
    } catch (e) {
      toast.error(e?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingState label="Loading conversations…" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div>
      <PageHeader title="Messages" description="Chat with guests about confirmed stays." />
      {threads.length === 0 ? (
        <Card className="p-16 text-center">
          <div className="mx-auto h-12 w-12 rounded-xl bg-slate-100 text-slate-500 grid place-items-center mb-3"><MessageSquare className="h-6 w-6" /></div>
          <div className="font-semibold text-slate-900">No conversations yet</div>
          <div className="text-sm text-slate-500 mt-1">Guest messages will appear here once you receive them.</div>
        </Card>
      ) : (
        <Card className="grid md:grid-cols-[320px_1fr] h-[calc(100vh-14rem)] overflow-hidden">
          <div className="border-r border-slate-200 flex flex-col min-h-0">
            <div className="p-3 border-b border-slate-200 relative"><Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={q} onChange={(event) => setQ(event.target.value)} className="pl-9 h-9 bg-slate-50 border-slate-200" placeholder="Search guest or property" /></div>
            <div className="overflow-y-auto">
              {filtered.map((item) => {
                const customer = item.customer || item.guest || {};
                const latest = item.latest_message;
                return (
                  <button key={item.id} onClick={() => setActiveId(item.id)} className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${Number(activeId) === Number(item.id) ? 'bg-teal-50/60' : ''}`}>
                    <div className="font-medium text-sm text-slate-900 truncate">{customer.name || `Booking #${item.id}`}</div>
                    <div className="text-xs text-slate-500 truncate">{item.property?.title || 'Property'}</div>
                    <div className="mt-1 text-xs text-slate-400 truncate">{latest?.message || 'Open conversation'}</div>
                    {Number(item.unread_chat_count || 0) > 0 && <span className="mt-1 inline-flex rounded-full bg-teal-700 px-2 py-0.5 text-[10px] font-semibold text-white">{item.unread_chat_count} unread</span>}
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No conversations match this search.</div>}
            </div>
          </div>

          <div className="flex flex-col min-w-0 min-h-0">
            {active ? (
              <>
                <div className="p-4 border-b border-slate-200">
                  <div className="font-semibold text-slate-900">{thread?.booking?.customer?.name || active.customer?.name || active.guest?.name || `Booking #${activeId}`}</div>
                  <div className="text-xs text-slate-500 truncate">{thread?.booking?.property?.title || active.property?.title} · Booking #{activeId}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-slate-50/40">
                  {loadingMsgs && <div className="text-center text-sm text-slate-500">Loading conversation…</div>}
                  {!loadingMsgs && messages.length === 0 && <div className="text-center text-sm text-slate-500">No messages yet.</div>}
                  {messages.map((message, index) => {
                    const fromHost = message.sender_type === 'admin';
                    return (
                      <div key={message.id || index} className={`flex ${fromHost ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${fromHost ? 'bg-teal-700 text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
                          <div>{message.message}</div>
                          <div className={`text-[10px] mt-1 ${fromHost ? 'text-teal-100' : 'text-slate-400'}`}>{message.created_at ? new Date(message.created_at).toLocaleString() : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="p-3 border-t border-slate-200 flex items-center gap-2">
                  <Input value={text} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); send(); } }} placeholder="Type your reply…" className="h-10" />
                  <Button onClick={send} disabled={!text.trim() || sending} className="bg-teal-700 hover:bg-teal-800 h-10"><Send className="h-4 w-4" /></Button>
                </div>
              </>
            ) : <div className="grid place-items-center h-full text-sm text-slate-500">Select a conversation to start</div>}
          </div>
        </Card>
      )}
    </div>
  );
}
